import { useState, useEffect } from 'react';
import { useMediaQuery } from '@uidotdev/usehooks';
import { AnimatePresence, motion } from 'framer-motion';
import config from '../../config.json';
import { calculatePrice, getCommissionStatus } from '../../lib';
import { priceTypes } from './lib';
import { theme } from '../../styles';
import {
  Carousel,
  ButtonCheck,
  ButtonGroup,
  RadioCounter,
  FormRow,
  Button,
  ButtonIcon,
  ButtonTextIcon,
  List,
  Main,
  MainContent,
  SideNav,
  Title
} from '../../components';
import { ArrowOutwardIcon } from '../../assets/svg';
import NSFWModal from './NSFWModal';
import { InfoWrapper, Info, PricingForm, PriceWrapper, VTuberInfo } from './style';

const Prices = () => {
  const commStatus = getCommissionStatus();
  /** States */
  const [priceType, setPriceType] = useState('Sketch');
  const [[render, span, background, nsfw, count], setPricing] = useState([
    'Sketch',
    'Half Body',
    false,
    false,
    1
  ]);
  const [price, setPrice] = useState(0);
  const [showRequest, setShowRequest] = useState(commStatus.includes('open'));
  const [modalOpen, setModalOpen] = useState(false);
  const [vTuberInd, setVTuberInd] = useState(0);

  /** Hooks */
  useEffect(() => {
    let defaultRender = 'Sketch';
    
    if (commStatus.includes('fullrender')) {
      defaultRender = 'Full Render';
    }
    if (commStatus.includes('sketch')) {
      defaultRender = 'Sketch';
    }

    if (priceType === 'Sketch') {
      setPricing(['Sketch', span, background, nsfw, count]);
    } else if (priceType === 'Full Render') {
      setPricing(['Full Render', span, background, nsfw, count]);
    } else if (priceType === 'Background') {
      if (render === 'VTuber Model') {
        if (commStatus.includes('fullrenderonly')) {
          setPricing(['Full Render', span, true, nsfw, count]);
        } else {
          setPricing(['Sketch', span, true, nsfw, count]);
        }
      } else {
        setPricing([defaultRender, span, true, nsfw, count]);
      }
    } else if (priceType === 'NSFW') {
      if (render === 'VTuber Model') {
        if (commStatus.includes('fullrenderonly')) {
          setPricing(['Full Render', span, background, true, count]);
        } else {
          setPricing(['Sketch', span, background, true, count]);
        }
      } else {
        setPricing([defaultRender, span, background, true, count]);
      }
    } else if (priceType === 'VTuber Model') {
      setPricing(['VTuber Model', span, false, false, 1]);
    }
    if (commStatus.includes('open')) {
      if (priceType === 'Sketch') {
        setShowRequest(commStatus.includes('sketch'));
      } else if (priceType === 'Full Render') {
        setShowRequest(commStatus.includes('fullrender'));
      } else if (priceType === 'VTuber Model') {
        setShowRequest(commStatus.includes('vtuber'));
      } else if (priceType === 'NSFW' || priceType === 'Background') {
        setShowRequest(!commStatus.includes('vtuberonly'));
      }
    }
  }, [priceType]);

  useEffect(() => {
    setPrice(calculatePrice(render, span, background, nsfw, count));
  }, [render, span, background, nsfw, count]);
  const isWidthXl = useMediaQuery(`only screen and (min-width: ${theme.breakpoints.width.xl})`);
  const isWidthLg = useMediaQuery(`only screen and (min-width: ${theme.breakpoints.width.lg})`);

  return (
    <>
      <AnimatePresence>
        <NSFWModal
          key={modalOpen}
          open={modalOpen}
          handleClose={accept => {
            setModalOpen(false);
            accept && setPriceType('NSFW');
          }}
        />
      </AnimatePresence>
      <Main $flexDirection="row">
        <SideNav
          items={priceTypes}
          active={priceType}
          handleSelect={select => {
            if (select === 'NSFW' && priceType !== 'NSFW') {
              setModalOpen(true);
            } else {
              setPriceType(select);
            }
          }}
        />
        <MainContent
          $flexDirection={isWidthLg ? 'row' : 'column'}
          $alignItems="center"
          initial={theme.fadeIn.initial}
          animate={theme.fadeIn.animate}
          transition={theme.fadeIn.transition(theme.delaySm)}
        >
          <Carousel
            images={priceTypes[priceType].images}
            onPage={page => {
              if (priceType === 'VTuber Model') setVTuberInd(page);
            }}
          />
          <InfoWrapper>
            <Info>
              <Title as="h2">{priceType}</Title>

              <motion.p
                initial={theme.fadeIn.initial}
                animate={theme.fadeIn.animate}
                transition={theme.fadeIn.transition(theme.delaySm)}
              >
                {config.pricesAndNotes[priceType].note}
              </motion.p>

              <List items={config.pricesAndNotes[priceType].disclaimers} />
              {priceType === 'VTuber Model' && (
                <VTuberInfo
                  initial={theme.fadeIn.initial}
                  animate={theme.fadeIn.animate}
                  transition={theme.fadeIn.transition(theme.delaySm)}
                >
                  <Title as="h5">Featured VTuber</Title>
                  <Button
                    type="Link"
                    style="fill"
                    color="brand"
                    size="md"
                    width="full"
                    to={config.pricesAndNotes['VTuber Model'].models[vTuberInd].twitter}
                    target="blank"
                  >
                    {config.pricesAndNotes['VTuber Model'].models[vTuberInd].name}
                    <ButtonIcon>
                      <ArrowOutwardIcon />
                    </ButtonIcon>
                  </Button>
                </VTuberInfo>
              )}
            </Info>
            <PricingForm
              initial={theme.fadeIn.initial}
              animate={theme.fadeIn.animate}
              transition={theme.fadeIn.transition(theme.delaySm)}
            >
              <ButtonGroup
                options={['Half Body', 'Full Body']}
                active={span}
                handleChange={val => setPricing([render, val, background, nsfw, count])}
              />
              <FormRow>
                {priceType !== 'NSFW' || priceType !== 'VTuber Model' ? (
                  <>
                    {(priceType === 'Sketch' || priceType === 'Full Render') && (
                      <ButtonCheck
                        label={isWidthXl ? 'Background' : 'BG'}
                        checked={background}
                        handleChange={val => setPricing([render, span, val, nsfw, count])}
                      />
                    )}
                    {priceType === 'Background' && (
                      <ButtonCheck
                        label="Full Render"
                        checked={render === 'Full Render'}
                        handleChange={val =>
                          setPricing([val ? 'Full Render' : 'Sketch', span, val, nsfw, count])
                        }
                      />
                    )}
                    {priceType !== 'VTuber Model' && (
                      <ButtonCheck
                        label="NSFW"
                        checked={nsfw}
                        handleChange={val => setPricing([render, span, background, val, count])}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {priceType !== 'VTuber Model' && (
                      <>
                        <ButtonCheck
                          label="Full Render"
                          checked={render === 'Full Render'}
                          handleChange={val =>
                            setPricing([val ? 'Full Render' : 'Sketch', span, val, nsfw, count])
                          }
                        />
                        <ButtonCheck
                          label="Background"
                          checked={background}
                          handleChange={val => setPricing([render, span, val, nsfw, count])}
                        />
                      </>
                    )}
                  </>
                )}
              </FormRow>
              {priceType !== 'VTuber Model' ? (
                <RadioCounter
                  label="Characters"
                  name="Amount of Characters"
                  max={3}
                  value={count}
                  handleChange={val => setPricing([render, span, background, nsfw, val])}
                />
              ) : (
                <RadioCounter
                  label="Additional Toggles"
                  name="Additional Toggles"
                  max={4}
                  value={count}
                  handleChange={val => setPricing([render, span, background, nsfw, val])}
                />
              )}
              {showRequest && (
                <Button
                  type="Link"
                  color="brand"
                  size="lg"
                  width="full"
                  justify="space-between"
                  to="/request"
                  state={{
                    render: render,
                    span: span,
                    background: background,
                    nsfw: nsfw,
                    count: count
                  }}
                >
                  Request
                  <ButtonTextIcon>{price}</ButtonTextIcon>
                </Button>
              )}
              {!showRequest && (
                <PriceWrapper>
                  <h6>Estimated Price</h6>
                  <h4>{price}</h4>
                </PriceWrapper>
              )}
            </PricingForm>
          </InfoWrapper>
        </MainContent>
      </Main>
    </>
  );
};

export default Prices;
